import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSessionStats } from '../contexts/SessionStatsContext';
import { useSessionNavigation } from '../utils/navigation';
import './QuestionDetail.css';
import arrowRightIcon from '../assets/images/icons/arrow-right.svg';
import correctIcon from '../assets/images/icons/correct.svg';
import incorrectIcon from '../assets/images/icons/incorrect.svg';
import noAnswerIcon from '../assets/images/icons/no-answer.svg';
import tripleDotsIcon from '../assets/images/icons/tripledots.svg';

const TripleDotsIcons = ({ width = 30, height = 38, style = {} }) => (
  <img
    src={tripleDotsIcon}
    alt="Fill in the blank"
    width={width}
    height={height}
    style={style}
  />
);

const subjectConfig = {
  'biologia': 'Biologia',
  'chimica-propedeutica-biochimica': 'Chimica e propedeutica biochimica',
  'chimica': 'Chimica',
  'fisica': 'Fisica'
};

function QuestionDetail() {
  const { subjectId, questionId } = useParams();
  const navigate = useNavigate();
  const { questionStatistics, loading, error } = useSessionStats();
  const { getSubjectUrl, getQuestionUrl } = useSessionNavigation();

  const getPercentageColor = (percentage) => {
    if (percentage >= 70) return 'var(--success-color)';
    if (percentage >= 50) return 'var(--warning-color)';
    return 'var(--error-color)';
  };

  const handleBackClick = () => {
    navigate(getSubjectUrl(subjectId));
  };

  if (loading) {
    return null; // Skeleton is shown by AdminLayout
  }

  if (error) {
    return (
      <div className='question-detail'>
        <div className="error-message">
          <p>Errore nel caricamento della domanda: {error.message}</p>
        </div>
      </div>
    );
  }

  const subjectName = subjectConfig[subjectId];
  if (!subjectName) {
    return (
      <div className='question-detail'>
        <div className="error-message">
          <p>Materia non trovata</p>
        </div>
      </div>
    );
  }

  // Filter questions for this subject and find the specific question
  const subjectQuestions = questionStatistics.filter(
    question => question.subject_name === subjectName
  );

  const currentQuestion = subjectQuestions.find(
    question => question.question_number === parseInt(questionId)
  );

  if (!currentQuestion) {
    return (
      <div className='question-detail'>
        <div className='question-detail-header'>
          <button
            className='question-detail-back-button'
            onClick={handleBackClick}
          >
            <span className='back-icon'>
              <img src={arrowRightIcon} alt='Indietro' />
            </span>
            <span className='back-text'>Indietro</span>
          </button>
        </div>
        <div className="error-message">
          <p>Domanda non trovata</p>
        </div>
      </div>
    );
  }

  // Navigation logic
  const currentIndex = subjectQuestions.findIndex(
    question => question.question_number === parseInt(questionId)
  );
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < subjectQuestions.length - 1;

  const handleNavigateQuestion = (direction) => {
    if (direction === 'prev' && hasPrev) {
      const prevQuestion = subjectQuestions[currentIndex - 1];
      navigate(getQuestionUrl(subjectId, prevQuestion.question_number));
    } else if (direction === 'next' && hasNext) {
      const nextQuestion = subjectQuestions[currentIndex + 1];
      navigate(getQuestionUrl(subjectId, nextQuestion.question_number));
    }
  };

  const answers = [];

  if (currentQuestion.answer_options && currentQuestion.answer_options.length > 0) {
    currentQuestion.answer_options.forEach((option) => {
      answers.push({
        text: option.option_text,
        percentage: option.percentage,
        count: option.count,
        isCorrect: option.option_text === currentQuestion.correct_answer
      });
    });
  }

  if (currentQuestion.open_answers_summary && currentQuestion.open_answers_summary.length > 0) {
    currentQuestion.open_answers_summary.forEach((answer) => {
      answers.push({
        text: answer.answer,
        percentage: answer.percentage,
        count: answer.count,
        isCorrect: answer.answer === currentQuestion.correct_answer
      });
    });
  }

  if (currentQuestion.no_answer_count > 0) {
    const totalResponses = currentQuestion.total_responses;
    const noAnswerPercentage = (currentQuestion.no_answer_count / totalResponses) * 100;
    answers.push({
      text: 'Nessuna risposta',
      percentage: noAnswerPercentage,
      count: currentQuestion.no_answer_count,
      isCorrect: false
    });
  }

  return (
    <div className='question-detail'>
      <div className='question-detail-header'>
        <button
          className='question-detail-back-button'
          onClick={handleBackClick}
        >
          <span className='back-icon'>
            <img src={arrowRightIcon} alt='Indietro' />
          </span>
          <span className='back-text'>Indietro</span>
        </button>
      </div>

      <div className='question-content'>
        <div className='question-detail-item'>
          <div className='question-content-wrapper'>
            <div className='question-left'>
              <span className='question-number'>Domanda {currentQuestion.question_number + 1}</span>
              <p className='question-text'>
                {currentQuestion.question_text.split(/\[:blank:\]/).map((part, index, array) => (
                  <React.Fragment key={index}>
                    {part}
                    {index < array.length - 1 && (
                      <span className="fill-blank" data-blank={index + 1} style={{
                        marginLeft: '10px', marginRight: '10px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        verticalAlign: 'top'
                      }}>
                        <TripleDotsIcons width={30} height={38} style={{
                          verticalAlign: 'top',
                          display: 'inline-block',
                          transform: 'translateY(-2px)'
                        }} />
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </p>
            </div>

            <div className='question-divider'></div>

            <div className='question-stats'>
              <span
                className='correct-percentage'
                style={{
                  color: getPercentageColor(currentQuestion.accuracy),
                }}
              >
                {currentQuestion.accuracy.toFixed(2)}%
              </span>
              <span className='stats-label'>risposte corrette</span>
            </div>
          </div>
        </div>

        <div className='answers-section'>
          <h3 className='section-title'>Risposte</h3>

          <div className='answers-list'>
            {answers.map((answer, index) => {
              const isNoAnswer = answer.text === 'Nessuna risposta';
              return (
                <div
                  key={index}
                  className={`answer-item ${answer.isCorrect
                      ? 'correct'
                      : isNoAnswer
                        ? 'no-answer'
                        : 'incorrect'
                    }`}
                >
                  <div className='answer-indicator'>
                    {answer.isCorrect ? (
                      <img
                        src={correctIcon}
                        alt='Corretto'
                        className='correct-icon'
                      />
                    ) : isNoAnswer ? (
                      <img
                        src={noAnswerIcon}
                        alt='Nessuna risposta'
                        className='no-answer-icon'
                      />
                    ) : (
                      <img
                        src={incorrectIcon}
                        alt='Errato'
                        className='incorrect-icon'
                      />
                    )}
                  </div>

                  <div className='answer-content'>
                    <span className='answer-text'>{answer.text}</span>
                    <div className='answer-stats'>
                      <div className='answer-progress-bar'>
                        <div
                          className='answer-progress-fill'
                          style={{
                            width:
                              answer.percentage <= 4
                                ? `calc(${answer.percentage}% + 20px)`
                                : `${answer.percentage}%`,
                          }}
                        >
                          <span
                            className='answer-percentage'
                            style={{
                              right: (() => {
                                const percentage = answer.percentage;
                                if (percentage <= 0) return '-65px';
                                if (percentage < 10) return '-75px';
                                if (percentage < 100) return '-90px';
                                return '-70px';
                              })(),
                            }}
                          >
                            {answer.percentage.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className='navigation-buttons'>
          <button
            className='button-secondary prev-btn'
            onClick={() => handleNavigateQuestion('prev')}
            disabled={!hasPrev}
          >
            Precedente
          </button>
          <button
            className='button-secondary next-btn'
            onClick={() => handleNavigateQuestion('next')}
            disabled={!hasNext}
          >
            Successiva
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuestionDetail;
